import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { useQuery } from '@apollo/react-hooks'
import { Redirect, useHistory } from 'react-router-dom'
import ReactSVG from 'react-svg'

import IN_APP_MESSAGES_QUERY from '../../../../graphql/queries/in-app-messages.gql'
import Button from '../../../components/button'
import Loader from '../../../components/loader'
import { canSelectMessageCta, useSelectMessageCta } from '../hooks/use-select-message-cta'
import { useMarkMessageRead } from '../hooks/use-mark-message-read'
import { usePollInterval } from '../hooks/use-poll-interval'
import { usePolling } from '../hooks/use-polling'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'
import { useIsPageVisible } from '../../../hooks/use-is-page-visible'
import { useScreenWidth } from '../../../hooks/use-screen-width'
import { useIsKid } from '../../../hooks/use-is-kid'
import { getModalLocation } from '../../../lib/modal'
import {
  IN_APP_MESSAGES,
  MODALS,
  SCREEN_SIZE_BREAKPOINTS,
  STATIC_SCREENS
} from '../../../constants'
import arrowDown from '../../../../images/down-arrow.svg'
import arrowUp from '../../../../images/up-arrow.svg'
import styles from './notification-hub.css'

/**
 * Notification hub: lists the account's in-app messages as an accordion.
 * Only one message is open at a time; opening another collapses it.
 *
 * Data:
 * - Polls IN_APP_MESSAGES_QUERY while the page is visible. Unlike the
 *   real-time toast, the query is not skipped in the background (skipping
 *   clears data and would flash the empty state); polling is only paused, and
 *   resumes with an immediate refetch when the page is visible again.
 *
 * Opening a message (expanding it):
 * - when the message is still UNREAD, marks it read for the account (all
 *   devices) via useMarkMessageRead.
 * - adds the id to locallyReadIds so the unread indicator clears immediately.
 *   This overrides the fetched status, so a poll that was already in flight
 *   when the message was opened can't bring the indicator back with stale
 *   UNREAD data, and stale data can't trigger a second mark-read.
 * - if mark-read fails, the id is removed from locallyReadIds. The server
 *   still has the message as UNREAD, so the indicator returns and the next
 *   open retries. The client never marks a message unread on the server.
 */
const NotificationHub = () => {
  const history = useHistory()
  const isAuthenticated = useIsAuthenticated()
  const isPageVisible = useIsPageVisible()
  const screenWidth = useScreenWidth()
  const pollInterval = usePollInterval(IN_APP_MESSAGES.HUB_POLL_INTERVAL)
  const selectMessageCta = useSelectMessageCta()
  const markMessageRead = useMarkMessageRead()
  const isKidProfile = useIsKid()
  const shouldSkipQuery = !isAuthenticated || isKidProfile
  const isDesktopView = screenWidth >= SCREEN_SIZE_BREAKPOINTS.MIN_768
  const [openMessageId, setOpenMessageId] = useState(null)
  const openMessageRef = useRef(null)
  const [locallyReadIds, setLocallyReadIds] = useState(() => new Set())
  const {
    data, loading, error, refetch
  } = useQuery(IN_APP_MESSAGES_QUERY, {
    fetchPolicy: 'network-only',
    skip: shouldSkipQuery
  })
  usePolling(refetch, pollInterval, !shouldSkipQuery && isPageVisible)
  const wasPageVisible = useRef(isPageVisible)

  useEffect(() => {
    if (!shouldSkipQuery && isPageVisible && !wasPageVisible.current) {
      refetch().catch(() => null)
    }
    wasPageVisible.current = isPageVisible
  }, [shouldSkipQuery, isPageVisible, refetch])

  const toggleMessage = useCallback(id => {
    setOpenMessageId(currentId => (currentId === id ? null : id))
  }, [])

  // Opening a message collapses the previously open one. When that one sat
  // above, the newly opened message moves up and can end up behind the sticky
  // header, so scroll it back below the header (its scroll-margin-top).
  // Runs before paint, so the jump isn't visible.
  useLayoutEffect(() => {
    const element = openMessageRef.current
    if (!element) return

    const { top } = element.getBoundingClientRect()
    const scrollMarginTop = parseFloat(window.getComputedStyle(element).scrollMarginTop) || 0
    if (top < scrollMarginTop) element.scrollIntoView({ block: 'start' })
  }, [openMessageId])

  const setLocallyRead = useCallback((id, isRead) => {
    setLocallyReadIds(ids => {
      const nextIds = new Set(ids)
      if (isRead) {
        nextIds.add(id)
      } else {
        nextIds.delete(id)
      }
      return nextIds
    })
  }, [])

  const onMessageToggle = useCallback((message, { isOpen, isUnread }) => {
    const { id } = message
    // mark read only when expanding, not collapsing
    if (!isOpen) {
      if (isUnread) setLocallyRead(id, true)
      // pass the effective status so stale UNREAD data doesn't send another
      // mark-read for a message already marked
      const markReadRequest = markMessageRead({
        ...message,
        status: isUnread ? 'UNREAD' : 'READ'
      })
      // restore the unread indicator if mark-read failed
      markReadRequest?.catch(() => setLocallyRead(id, false))
    }
    toggleMessage(id)
  }, [markMessageRead, setLocallyRead, toggleMessage])

  const messages = data?.inAppMessages ?? []
  const isMessageUnread = message => (
    message.status === 'UNREAD' && !locallyReadIds.has(message.id)
  )
  const unreadCount = messages.filter(isMessageUnread).length

  // Signed-out visitors get the login modal over the hub
  if (!isAuthenticated) {
    return (
      <Redirect
        to={getModalLocation(
          { pathname: STATIC_SCREENS.NOTIFICATIONS.screenId },
          MODALS.qsParams.login
        )}
      />
    )
  }

  if (isKidProfile) return <Redirect to="/" />

  const renderContent = () => {
    if (loading) return <Loader />

    // a failed poll keeps the last successful list (useQuery still returns the
    // previous data with the error); only block when there is nothing to show
    if (error && !messages.length) {
      return (
        <p className={styles.errorStatus} role="alert">
          Notifications could not be loaded. Refresh the page to try again.
        </p>
      )
    }

    if (!messages.length) {
      return (
        <div className={styles.emptyState}>
          <p className={styles.header}>Nothing to see here!</p>
          <p className={styles.status}>
            When you receive notifications they will appear here
          </p>
          <Button
            className={styles.browserButton}
            text="BROWSE NEON"
            onClick={() => history.push('/')}
          />
        </div>
      )
    }

    return (
      <div className={styles.messages}>
        {messages.map(message => {
          const {
            id,
            header,
            message: body,
            ctaText,
            startsAt,
            startsAtText,
            thumbnail
          } = message
          const isOpen = openMessageId === id
          const detailsId = `notification-details-${id}`
          const isUnread = isMessageUnread(message)

          // rendered next to the body on desktop, below the thumbnail on mobile
          const ctaButton = canSelectMessageCta(message) && (
            <Button
              wrapperClassName={styles.ctaButtonWrapper}
              className={styles.ctaButton}
              e2eIdentifier={`in-app-message-cta-${id}`}
              onClick={() => selectMessageCta({
                ...message,
                status: isUnread ? 'UNREAD' : 'READ'
              })}
              text={ctaText}
            />
          )

          return (
            <div
              className={styles.accordionWrapper}
              key={id}
              ref={isOpen ? openMessageRef : undefined}
            >
              <button
                type="button"
                className={styles.metadata}
                aria-expanded={isOpen}
                aria-controls={detailsId}
                data-lbx-e2e={`notification-toggle-${id}`}
                onClick={() => onMessageToggle(message, { isOpen, isUnread })}
              >
                <span className={styles.summary}>
                  {startsAtText && (
                    <span className={styles.timeRow}>
                      { isUnread && <span className={styles.unreadIndicator} aria-hidden="true" /> }
                      <time className={styles.timestamp} dateTime={startsAt}>
                        {startsAtText}
                      </time>
                    </span>
                  )}
                  {header && <span className={styles.messageHeader}>{header}</span>}
                </span>
                <ReactSVG
                  src={isOpen ? arrowUp : arrowDown}
                  wrapper="span"
                  beforeInjection={svg => {
                    svg.setAttribute('class', styles.arrowIcon)
                  }}
                />
              </button>
              {isOpen && (
                <div className={styles.details} id={detailsId}>
                  <div className={styles.detailsText}>
                    {body && <p className={styles.body}>{body}</p>}
                    {isDesktopView && ctaButton}
                  </div>
                  {thumbnail && (
                    <img
                      key={thumbnail}
                      className={styles.thumbnail}
                      src={thumbnail}
                      alt=""
                      loading="lazy"
                      onError={e => { e.currentTarget.hidden = true }}
                    />
                  )}
                  {!isDesktopView && ctaButton}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <h1 className={styles.title}>
        Notifications
      </h1>

      {messages.length > 0 && (
        <span className={styles.unreadCount}>
          {`${unreadCount} Unread`}
        </span>
      )}

      <div className={styles.page}>
        {renderContent()}
      </div>
    </>
  )
}

export default NotificationHub
